/*
 * MEGAKRUNCH product-page changelog (sections/mk-changelog.liquid)
 *
 * When the section's "Product slug" setting is filled in, the section renders
 * the product's release notes from the license-proxy's public
 *   GET <license_api_base>/changelog?product=<slug>
 * instead of the hand-typed richtext. The response is the site view of the
 * plugin's CHANGELOG.md, written to S3 by the release pipeline. Contract:
 * megakrunch-hq docs/plugin-dev/release-notes-spec.md, sections 3 and 4:
 *
 *   { "product": "glitch-control",
 *     "entries": [ { "version": "1.1.2", "date": "2026-10-02", "notes": ["..."] },
 *                  { "version": "1.0", "date": null, "notes": ["..."] } ] }
 *
 * The markup matches the richtext the section used to hold, so the existing
 * .mk-changelog__content rules style it unchanged:
 *   <p><strong>Version 1.1.2</strong> <span class="mk-changelog__date">Oct 2, 2026</span><br>- note<br>- note</p>
 *
 * The section ships hidden and is shown only after a good response. Any
 * failure (network, CORS, non-200, a body that is not the shape above, zero
 * usable entries) leaves the whole section hidden, heading included.
 *
 * SAFETY RULE: the notes are outside-the-theme data (S3). Nothing from the
 * response is ever written as HTML: every string goes in through textContent
 * or createTextNode.
 *
 * The same response fills the VERSION row of the product block's spec readout
 * (snippets/mk-readout.liquid): the newest entry's version and date, as
 *   <span class="mk-readout__version">1.1.2</span>, Aug 18, 2026
 * The row ships hidden and stays hidden on any failure, so a stale or missing
 * version never shows. A page holding both makes one request per product.
 *
 * The pure functions are exported for node (scripts/test-changelog.test.js).
 */
(function (root) {
  'use strict';

  var VERSION = /^\d+\.\d+(\.\d+)?$/;
  var DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // "2026-10-02" -> "Oct 2, 2026". Anything else (null, junk) -> null, so no
  // date is shown. Formatted by hand: no Date object, so no time zone can
  // move the day.
  function formatDate(value) {
    if (typeof value !== 'string') return null;
    var m = DATE.exec(value);
    if (!m) return null;
    var month = parseInt(m[2], 10);
    var day = parseInt(m[3], 10);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return MONTHS[month - 1] + ' ' + day + ', ' + m[1];
  }

  function compareVersions(a, b) {
    var pa = a.split('.');
    var pb = b.split('.');
    for (var i = 0; i < 3; i++) {
      var x = parseInt(pa[i] || '0', 10);
      var y = parseInt(pb[i] || '0', 10);
      if (x !== y) return x - y;
    }
    return 0;
  }

  // Validates a /changelog response. Returns the usable entries newest first
  // ({ version, date, notes }), or null when there are none. An entry with a
  // bad version or no non-empty note is dropped; a body that is not the
  // contract's shape (or is for another product) is rejected whole.
  function parseChangelog(data, product) {
    if (!data || typeof data !== 'object' || !Array.isArray(data.entries)) return null;
    if (product && data.product !== product) return null;
    var out = [];
    for (var i = 0; i < data.entries.length; i++) {
      var e = data.entries[i];
      if (!e || typeof e.version !== 'string' || !VERSION.test(e.version)) continue;
      if (!Array.isArray(e.notes)) continue;
      var notes = [];
      for (var j = 0; j < e.notes.length; j++) {
        if (typeof e.notes[j] === 'string' && e.notes[j].trim() !== '') notes.push(e.notes[j].trim());
      }
      if (!notes.length) continue;
      out.push({ version: e.version, date: formatDate(e.date), notes: notes, index: i });
    }
    if (!out.length) return null;
    // Newest first, as the file already is; a stable sort guards a hand edit.
    out.sort(function (a, b) { return compareVersions(b.version, a.version) || a.index - b.index; });
    return out.map(function (e) { return { version: e.version, date: e.date, notes: e.notes }; });
  }

  // Fills `container` with one <p> per entry. Text nodes only.
  function render(doc, container, entries) {
    while (container.firstChild) container.removeChild(container.firstChild);
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      var p = doc.createElement('p');
      var strong = doc.createElement('strong');
      strong.textContent = 'Version ' + entry.version;
      p.appendChild(strong);
      if (entry.date) {
        p.appendChild(doc.createTextNode(' '));
        var date = doc.createElement('span');
        date.className = 'mk-changelog__date';
        date.textContent = entry.date;
        p.appendChild(date);
      }
      for (var j = 0; j < entry.notes.length; j++) {
        p.appendChild(doc.createElement('br'));
        p.appendChild(doc.createTextNode('- ' + entry.notes[j]));
      }
      container.appendChild(p);
    }
  }

  function changelogUrl(apiBase, product) {
    return String(apiBase).replace(/\/+$/, '') + '/changelog?product=' + encodeURIComponent(product);
  }

  // The newest release ({ version, date }) from parseChangelog's output, which
  // is already newest first; null when there is none.
  function newestRelease(entries) {
    if (!Array.isArray(entries) || !entries.length) return null;
    return { version: entries[0].version, date: entries[0].date || null };
  }

  // Fills the readout's VERSION value: the version in its own span (mono),
  // then ", <date>" as plain text when the entry has a date. Text nodes only.
  function renderReadoutVersion(doc, value, release) {
    while (value.firstChild) value.removeChild(value.firstChild);
    var version = doc.createElement('span');
    version.className = 'mk-readout__version';
    version.textContent = release.version;
    value.appendChild(version);
    if (release.date) value.appendChild(doc.createTextNode(', ' + release.date));
  }

  var api = {
    formatDate: formatDate,
    compareVersions: compareVersions,
    parseChangelog: parseChangelog,
    render: render,
    changelogUrl: changelogUrl,
    newestRelease: newestRelease,
    renderReadoutVersion: renderReadoutVersion
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
  if (!root || !root.document || typeof root.fetch !== 'function') return;
  // The section and the readout block each load this file; the first copy to
  // run boots every node on the page, so a second copy does nothing.
  if (root.__mkChangelogLoaded) return;
  root.__mkChangelogLoaded = true;

  // One request per product, shared by the section and the readout. Resolves
  // to the parsed entries; rejects on any failure.
  var requests = {};
  function load(apiBase, product) {
    var url = changelogUrl(apiBase, product);
    if (!requests[url]) {
      requests[url] = root.fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (res) {
          if (!res.ok) throw new Error('changelog_http_' + res.status);
          return res.json();
        })
        .then(function (data) {
          var entries = parseChangelog(data, product);
          if (!entries) throw new Error('changelog_empty');
          return entries;
        });
    }
    return requests[url];
  }

  function warn(err) {
    // The message only, never a response body.
    try { if (root.console) root.console.warn('[changelog] hidden: ' + ((err && err.message) || 'error')); } catch (e) { /* never block */ }
  }

  function initReadout(row) {
    var apiBase = row.getAttribute('data-api');
    var product = row.getAttribute('data-product');
    var value = row.querySelector('[data-mk-readout-version-value]');
    if (!apiBase || !product || !value) return;
    load(apiBase, product)
      .then(function (entries) {
        var release = newestRelease(entries);
        if (!release) throw new Error('changelog_empty');
        renderReadoutVersion(root.document, value, release);
        row.hidden = false;
      })
      .catch(warn);
  }

  function init(section) {
    var apiBase = section.getAttribute('data-api');
    var product = section.getAttribute('data-product');
    var content = section.querySelector('[data-mk-changelog-content]');
    if (!apiBase || !product || !content) return;
    load(apiBase, product)
      .then(function (entries) {
        render(root.document, content, entries);
        section.hidden = false;
      })
      // The section stays hidden.
      .catch(warn);
  }

  function boot() {
    var nodes = root.document.querySelectorAll('[data-mk-changelog]');
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
    var rows = root.document.querySelectorAll('[data-mk-readout-version]');
    for (var j = 0; j < rows.length; j++) initReadout(rows[j]);
  }

  if (root.document.readyState === 'loading') {
    root.document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})(typeof window !== 'undefined' ? window : this);
